import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import MarketingPageHeader from '../components/marketing/MarketingPageHeader';
import { useMarketingEmbedded } from '../hooks/useMarketingEmbedded';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyId } from '../hooks/useCompanyId';
import { 
  createWebsiteAudit, 
  getUserAudits, 
  transformAuditResults,
  pollAuditStatus,
  getUserAuditProgress,
  clearAuditProgress,
  cancelAudit,
  saveWebsiteManagementRequest,
  getUserWebsiteData,
  cancelWebsiteManagementRequest,
  getWebsiteManagementRequestStatus
} from '../services/websiteAuditService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { Landmark, CreditCard, Search, Globe, TrendingUp, Hash, Link2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { getAccountById, updateAccount } from '../services/firestoreService';
import { useDevMode } from '../hooks/useDevMode';
import AiSearchDevDiagnostics from '../components/dev/AiSearchDevDiagnostics';
import { collection, query, where, orderBy, limit, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { sanitizeAlaCarteForPublic } from '../utils/alaCartePublic';
import PortalBackground from '../components/PortalBackground';

// overrideCompanyId/overrideUserId let an internal viewer (CRM Portal View)
// load a specific client account's website audit instead of their own.
const Website = ({ embedded: embeddedProp, overrideCompanyId = null, overrideUserId = null }) => {
  const embedded = useMarketingEmbedded(embeddedProp);
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { companyId: resolvedCompanyId } = useCompanyId();
  const companyId = overrideCompanyId || resolvedCompanyId;
  // The uid whose audits / website data / requests are read/written.
  const effectiveUid = overrideUserId || currentUser?.uid;
  const { devMode } = useDevMode();

  // States
  const [auditData, setAuditData] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  const [selectedPage, setSelectedPage] = useState(null);
  const [showPageModal, setShowPageModal] = useState(false);
  const [showNewAuditModal, setShowNewAuditModal] = useState(false);
  const [newAuditUrl, setNewAuditUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [auditProgress, setAuditProgress] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('success');
  const [auditInProgress, setAuditInProgress] = useState(false);
  const [currentAuditUrl, setCurrentAuditUrl] = useState('');
  const [auditProgressId, setAuditProgressId] = useState(null);
  const [showManagementPopup, setShowManagementPopup] = useState(false);
  const [managementRequestSubmitted, setManagementRequestSubmitted] = useState(false);
  const [submittedRequestData, setSubmittedRequestData] = useState(null);
  const [marketingRequestStatus, setMarketingRequestStatus] = useState(null);
  const [initialStatusLoad, setInitialStatusLoad] = useState(true);
  const [managementFormData, setManagementFormData] = useState({
    selectedService: '', // single a la carte option: SEO Management, Templated Website + SEO Management, or Custom Website + SEO Management
    websiteUrl: '',
    startDate: ''
  });
  const [websiteAlaCarteOptions, setWebsiteAlaCarteOptions] = useState([]);
  const [websiteAlaCarteLoading, setWebsiteAlaCarteLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [newAuditKeywords, setNewAuditKeywords] = useState('');
  const [accountProfile, setAccountProfile] = useState(null);
  const [websiteManagementCheckoutLoading, setWebsiteManagementCheckoutLoading] = useState(false);
  const [websiteManagementCheckoutError, setWebsiteManagementCheckoutError] = useState(null);

  // InfoIcon Component with Tooltip
  const InfoIcon = ({ description, tooltipId }) => {
    const isActive = activeTooltip === tooltipId;
    const [clickedOpen, setClickedOpen] = useState(false);
    
    // Reset clickedOpen when this tooltip is no longer active
    useEffect(() => {
      if (!isActive && clickedOpen) {
        setClickedOpen(false);
      }
    }, [isActive, clickedOpen]);
    
    return (
     <div className="relative inline-block ml-2 info-icon-container">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (isActive) {
              setActiveTooltip(null);
              setClickedOpen(false);
            } else {
              setActiveTooltip(tooltipId);
              setClickedOpen(true);
            }
          }}
          onMouseEnter={() => {
            if (!clickedOpen) {
              setActiveTooltip(tooltipId);
            }
          }}
          onMouseLeave={() => {
            if (!clickedOpen) {
              setActiveTooltip(null);
            }
          }}
          className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-medium transition-colors cursor-pointer ${
            darkMode 
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
          aria-label="Information"
        >
          ℹ️
        </button>
        {isActive && (
         <div
           className={`absolute z-50 w-64 max-w-full p-3 rounded-lg shadow-lg text-sm ${
             darkMode 
               ? 'bg-gray-900 text-white border border-gray-700' 
               : 'bg-white text-gray-800 border border-gray-200'
           }`}
           style={{
             bottom: '100%',
             left: '50%',
             transform: 'translateX(-50%)',
             marginBottom: '8px',
             maxWidth: 'min(280px, 90vw)',
             whiteSpace: 'normal'
           }}
            onMouseEnter={() => setActiveTooltip(tooltipId)}
            onMouseLeave={() => {
              if (!clickedOpen) {
                setActiveTooltip(null);
              }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-xs leading-relaxed">{description}</div>
            <div
              className={`absolute w-0 h-0 border-4 ${
                darkMode 
                  ? 'border-t-gray-900' 
                  : 'border-t-white'
              }`}
              style={{
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                borderLeftColor: 'transparent',
                borderRightColor: 'transparent',
                borderBottomColor: 'transparent'
              }}
            />
          </div>
        )}
      </div>
    );
  };

  // Stripe checkout for Website Management (similar to strategy calls)
  const handleWebsiteManagementCheckout = async (paymentMethod) => {
    if (!currentUser) {
      setError('You must be logged in to pay for website management.');
      return;
    }
    if (!managementFormData.selectedService) {
      setError('Please select one website service option.');
      return;
    }
    if (!managementFormData.websiteUrl) {
      setError('Please enter your website URL.');
      return;
    }

    // Validate start date: required, at least next day, and weekday (Mon–Fri)
    if (!managementFormData.startDate) {
      setError('Please select a start date.');
      return;
    }
    const selectedDate = new Date(managementFormData.startDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (selectedDate < tomorrow) {
      setError('Start date must be at least the next business day.');
      return;
    }
    const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 6 = Saturday
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      setError('Start date must be on a weekday (Monday–Friday).');
      return;
    }

    try {
      setError(null);
      setWebsiteManagementCheckoutError(null);
      setWebsiteManagementCheckoutLoading(true);

      // First, persist the management request details so fulfillment has context
      const payload = {
        selectedServices: [managementFormData.selectedService],
        websiteUrl: managementFormData.websiteUrl || '',
        startDate: managementFormData.startDate || '',
        monthlyBudget: '',
        priorityLevel: ''
      };
      const result = await saveWebsiteManagementRequest(effectiveUid, payload);
      const managementRequest =
        result?.managementRequest || { ...payload, submittedAt: new Date().toISOString(), status: 'requested' };

      setSubmittedRequestData(managementRequest);
      setManagementRequestSubmitted(true);

      // Determine pricing for the selected website management service
      const selectedItem =
        websiteAlaCarteOptions.find(
          (opt) =>
            typeof opt.name === 'string' &&
            opt.name.trim().toLowerCase() === managementFormData.selectedService.trim().toLowerCase()
        ) || null;

      const baseCost = selectedItem?.standardPricing?.cost != null ? Number(selectedItem.standardPricing.cost) : 0;
      if (!Number.isFinite(baseCost) || baseCost <= 0) {
        setWebsiteManagementCheckoutError('Pricing is not configured for this website service yet.');
        setWebsiteManagementCheckoutLoading(false);
        return;
      }

      const baseAmountCents = Math.round(baseCost * 100);
      const isCard = (paymentMethod || 'ach').toLowerCase() === 'card';
      const amountCents = isCard ? Math.round(baseAmountCents * 1.03) : baseAmountCents;

      if (amountCents < 50) {
        setWebsiteManagementCheckoutError('Charge amount is too low to process.');
        setWebsiteManagementCheckoutLoading(false);
        return;
      }

      // Then start Stripe Checkout using generic createCheckoutSession
      const createSession = httpsCallable(functions, 'createCheckoutSession');
      const isPortal = window.location.pathname.startsWith('/portal');
      const returnPath = isPortal ? '/portal/checkout/return' : '/start/checkout/return';

      const { data } = await createSession({
        amount: amountCents,
        paymentMethod: paymentMethod || 'ach',
        currency: 'usd',
        programName: `Website Management - ${managementFormData.selectedService}`,
        billingCycle: 'one-time',
        productType: 'website_management',
        // The server prices from this catalog name; `amount` is only compared, never charged.
        selectedService: managementFormData.selectedService,
        productQuantity: 1,
        customerEmail: currentUser.email,
        returnPath,
        origin: window.location.origin,
        startDate: managementFormData.startDate,
        ...(companyId && { companyId }),
      });

      if (data?.url) {
        window.location.href = data.url;
      } else {
        setWebsiteManagementCheckoutError('No checkout URL received from Stripe.');
        setWebsiteManagementCheckoutLoading(false);
      }
    } catch (err) {
      console.error('Error starting website management checkout:', err);
      setWebsiteManagementCheckoutError(err.message || 'Failed to start checkout. Please try again.');
      setWebsiteManagementCheckoutLoading(false);
    }
  };

  // Helper function for score color classes
  const getScoreColorClass = (score) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-blue-500";
    if (score >= 40) return "bg-yellow-500";
    return "bg-red-500";
  };

  // Helper function for status badge styling
  const getStatusBadge = (status) => {
    const statusConfig = {
      'requested': { bg: 'bg-yellow-100 text-yellow-800', text: 'Requested' },
      'pending': { bg: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      'approved': { bg: 'bg-green-100 text-green-800', text: 'Approved' },
      'denied': { bg: 'bg-red-100 text-red-800', text: 'Denied' },
      'in-progress': { bg: 'bg-blue-100 text-blue-800', text: 'In Progress' },
      'active': { bg: 'bg-purple-100 text-purple-800', text: 'Active' },
      'completed': { bg: 'bg-purple-100 text-purple-800', text: 'Completed' },
      'cancelled': { bg: 'bg-red-100 text-red-800', text: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig['requested'];
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg}`}>
        {config.text}
      </span>
    );
  };

  const getFixTypeColor = (fixType) => {
    switch (fixType) {
      case 'Critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'SEO': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Performance': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Structure': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Accessibility': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Convert a 0-100 score into a letter grade + color name.
  // Lenient 20-point bands: A 80-100, B 60-80, C 40-60, D 20-40, F 0-20.
  // Each band is split into thirds for +/- (top third = +, bottom third = -).
  const scoreToGrade = (score) => {
    if (score == null || Number.isNaN(Number(score))) return { letter: 'N/A', color: 'gray' };
    const s = Math.max(0, Math.min(100, Number(score)));
    const bands = [
      { min: 80, base: 'A', color: 'green' },
      { min: 60, base: 'B', color: 'green' },
      { min: 40, base: 'C', color: 'yellow' },
      { min: 20, base: 'D', color: 'yellow' },
      { min: 0, base: 'F', color: 'red' },
    ];
    const band = bands.find((b) => s >= b.min);
    if (band.base === 'F') return { letter: 'F', color: 'red' };
    const within = s - band.min; // 0..20 within the band
    let suffix = '';
    if (within >= 40 / 3) suffix = '+';       // top third
    else if (within < 20 / 3) suffix = '-';   // bottom third
    return { letter: `${band.base}${suffix}`, color: band.color };
  };

  // Tailwind classes for a color name used by grade pills
  const gradeColorClasses = (color) => {
    const map = {
      green: darkMode ? 'bg-green-900 text-green-200 border-green-600' : 'bg-green-100 text-green-700 border-green-400',
      yellow: darkMode ? 'bg-yellow-900 text-yellow-200 border-yellow-600' : 'bg-yellow-100 text-yellow-700 border-yellow-400',
      red: darkMode ? 'bg-red-900 text-red-200 border-red-600' : 'bg-red-100 text-red-700 border-red-400',
      gray: darkMode ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-600 border-gray-300',
    };
    return map[color] || map.gray;
  };

  // Build a plain-English summary + prioritized action items from audit data (deterministic, no AI cost)
  const buildPlainSummary = (data) => {
    if (!data || data.seoScore == null) {
      return {
        headline: 'Run your first audit to see how your website performs in search.',
        tone: 'neutral',
        actions: [],
      };
    }
    const seo = Number(data.seoScore) || 0;
    const tone = seo >= 80 ? 'good' : seo >= 55 ? 'fair' : 'poor';
    const headline = tone === 'good'
      ? 'Your website is in good shape for search.'
      : tone === 'fair'
        ? "Your website is doing okay, but there's clear room to grow."
        : 'Your website needs attention to compete in search.';

    const candidates = [];
    const ew = data.errorsWarnings || {};
    if ((ew.criticalErrors || 0) > 0) {
      candidates.push({ weight: 100, text: `Fix ${ew.criticalErrors} critical error${ew.criticalErrors !== 1 ? 's' : ''} (broken links or pages) that hurt your rankings and frustrate visitors.` });
    }
    const ai = data.aiSearch;
    if (ai && (ai.totalAiVolume || 0) > 0 && !ai.llmMentions?.cited) {
      candidates.push({ weight: 85, text: "Get cited by AI tools — people are asking AI assistants about your services, but they aren't recommending you yet." });
    }
    if ((data.technicalScore ?? data.pageHealth ?? 100) < 70) {
      candidates.push({ weight: 80, text: 'Improve technical health — speed up pages and fix crawl issues so search engines can properly index your site.' });
    }
    if ((data.pageAudit?.loadSpeed || 0) > 3) {
      candidates.push({ weight: 75, text: `Speed up your site — pages take ${data.pageAudit.loadSpeed}s to load; aim for under 3 seconds.` });
    }
    if ((data.onPageScore ?? data.seoScore ?? 100) < 70) {
      candidates.push({ weight: 70, text: 'Strengthen on-page SEO — clearer titles, meta descriptions, and headings on your most important pages.' });
    }
    if ((data.backlinkProfile?.backlinks || 0) < 10) {
      candidates.push({ weight: 60, text: 'Build more quality backlinks — get listed in local directories and earn links from trusted sites.' });
    }
    if ((data.rankedKeywords?.top10 || 0) < 5) {
      candidates.push({ weight: 50, text: 'Target more keywords — create content around the services and questions your customers search for.' });
    }

    let actions = candidates.sort((a, b) => b.weight - a.weight).slice(0, 3).map((c) => c.text);
    if (actions.length === 0) {
      actions = ['Keep publishing fresh, helpful content to maintain and grow your rankings.'];
    }
    return { headline, tone, actions };
  };

  // Plain-English strengths & concerns for the Simple view
  const buildHighlights = (data) => {
    if (!data || data.seoScore == null) return { working: [], attention: [] };
    const working = [];
    const attention = [];
    const technical = data.technicalScore ?? data.pageHealth;
    const onPage = data.onPageScore ?? data.seoScore;
    const backlinks = data.backlinkProfile?.backlinks || 0;
    const critical = data.errorsWarnings?.criticalErrors || 0;
    const top10 = data.rankedKeywords?.top10 || 0;
    const speed = data.pageAudit?.loadSpeed || 0;

    if (critical === 0) working.push('No critical errors — nothing on your site is actively broken.');
    else attention.push(`${critical} critical error${critical !== 1 ? 's' : ''} (broken links or pages) need fixing right away.`);

    if (technical != null) {
      if (technical >= 75) working.push('Strong technical foundation — search engines can crawl and index your site.');
      else attention.push('Technical health is holding you back — speed, crawlability, and indexing need work.');
    }
    if (speed > 0) {
      if (speed <= 3) working.push(`Pages load fast (${speed}s) — good for visitors and rankings.`);
      else attention.push(`Pages are slow (${speed}s to load) — visitors leave and rankings suffer.`);
    }
    if (onPage != null) {
      if (onPage >= 75) working.push('Your pages are well-optimized with clear titles and content.');
      else attention.push('On-page SEO needs work — clearer titles, descriptions, and headings.');
    }
    if (backlinks >= 50) working.push(`${backlinks.toLocaleString()} sites link to yours, building trust with Google.`);
    else attention.push('Few sites link to yours — earning links from trusted sites builds authority.');

    if (top10 >= 5) working.push(`${top10} of your keywords already rank on page one of Google.`);
    else attention.push('Few page-one rankings — target more of the searches your customers make.');

    if (data.aiSearch?.llmMentions?.cited) working.push('AI tools like ChatGPT already recommend your business.');

    return { working: working.slice(0, 4), attention: attention.slice(0, 4) };
  };

  // Tiny inline sparkline for 12-month AI search volume trends
  const Sparkline = ({ trend }) => {
    const points = Array.isArray(trend) ? trend.map((t) => t.volume || 0) : [];
    if (points.length < 2) {
      return <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>—</span>;
    }
    const w = 80;
    const h = 24;
    const max = Math.max(...points, 1);
    const min = Math.min(...points);
    const range = max - min || 1;
    const coords = points.map((v, i) => {
      const x = (i / (points.length - 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    const last = points[points.length - 1];
    const first = points[0];
    const stroke = last > first ? '#16a34a' : last < first ? '#dc2626' : (darkMode ? '#9ca3af' : '#6b7280');
    return (
      <svg width={w} height={h} className="inline-block align-middle" aria-hidden="true">
        <polyline points={coords.join(' ')} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
    );
  };

  // Show notification
  const showNotificationMessage = (message, type = 'success') => {
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 5000);
  };

  // Website management a la carte options (SEO Management, Templated Website + SEO Management, Custom Website + SEO Management)
  const WEBSITE_ALA_CARTE_NAMES = [
    'SEO Management',
    'Templated Website + SEO Management',
    'Custom Website + SEO Management'
  ];
  const SEO_MANAGEMENT_FALLBACK_DESCRIPTION =
    'Ongoing SEO management, technical optimization, and reporting to grow qualified traffic.';

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setWebsiteAlaCarteLoading(true);
    getDocs(collection(db, 'alaCarteItems'))
      .then((snapshot) => {
        if (cancelled) return;
        const items = snapshot.docs.map((d) => sanitizeAlaCarteForPublic({ id: d.id, ...d.data() }));
        setWebsiteAlaCarteOptions(items);
      })
      .catch((err) => {
        if (!cancelled) console.error('Error loading website a la carte options:', err);
      })
      .finally(() => {
        if (!cancelled) setWebsiteAlaCarteLoading(false);
      });
    return () => { cancelled = true; };
  }, [companyId]);

  // Load shared company profile (website URL + saved target keywords) for audit prefill
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    getAccountById(companyId)
      .then((account) => {
        if (!cancelled) setAccountProfile(account);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [companyId]);

  // Open the audit modal with URL + keywords prefilled from the company profile
  const openNewAuditModal = () => {
    if (!newAuditUrl.trim() && accountProfile?.website) {
      setNewAuditUrl(accountProfile.website);
    }
    if (!newAuditKeywords.trim()) {
      const saved = Array.isArray(accountProfile?.targetKeywords)
        ? accountProfile.targetKeywords.join(', ')
        : (typeof accountProfile?.targetKeywords === 'string' ? accountProfile.targetKeywords : '');
      const fromLastAudit = (auditData?.aiSearch?.keywords || [])
        .map((k) => k.keyword)
        .filter(Boolean)
        .join(', ');
      const prefill = saved || fromLastAudit;
      if (prefill) setNewAuditKeywords(prefill);
    }
    setShowNewAuditModal(true);
  };

  const getWebsiteAlaCartePrice = (item) => {
    if (!item || typeof item !== 'object') return 'Pricing to be confirmed';

    const toCurrency = (n) => {
      const num = Number(n);
      if (!Number.isFinite(num)) return null;
      return `$${num.toLocaleString()}`;
    };

    // Always-included monthly style
    if (item.pricingType === 'alwaysIncluded' && item.standardPricing?.cost != null) {
      const price = toCurrency(item.standardPricing.cost);
      if (price) return `${price}/month`;
    }

    // Standard pricing
    if (item.standardPricing?.cost != null) {
      const price = toCurrency(item.standardPricing.cost);
      if (price) {
        const unit = item.standardPricing.unit || 'item';
        return `${price} per ${unit}`;
      }
    }

    // Packages: show a simple "From $X" using the cheapest package total
    if (Array.isArray(item.packages) && item.packages.length > 0) {
      const totals = item.packages
        .map((pkg) =>
          (pkg.items || []).reduce((sum, it) => {
            const cost = Number(it.cost);
            return Number.isFinite(cost) ? sum + cost : sum;
          }, 0)
        )
        .filter((t) => Number.isFinite(t) && t > 0);
      if (totals.length > 0) {
        const min = Math.min(...totals);
        const price = toCurrency(min);
        if (price) return `From ${price}`;
      }
    }

    return 'Pricing to be confirmed';
  };

  // Manual refresh of marketing request status
  const refreshMarketingRequestStatus = async () => {
    if (!currentUser?.uid) return;
    
    try {
      setRefreshingStatus(true);
      console.log('🔄 Manually refreshing marketing request status...');
      
      const statusResult = await getWebsiteManagementRequestStatus(effectiveUid);
      console.log('🔄 Manual refresh result:', statusResult);
      
      if (statusResult.success && statusResult.request) {
        setMarketingRequestStatus(statusResult.request);
        console.log('🔄 Updated marketingRequestStatus from manual refresh:', statusResult.request.status);
        showNotificationMessage('✅ Status refreshed successfully!', 'success');
      } else {
        console.log('🔄 No marketing request found in manual refresh');
        setMarketingRequestStatus(null);
      }
    } catch (error) {
      console.error('🔄 Error in manual refresh:', error);
      showNotificationMessage('❌ Failed to refresh status', 'error');
    } finally {
      setRefreshingStatus(false);
    }
  };


  // Cancel audit function
  const handleCancelAudit = async () => {
    try {
      console.log('🛑 Cancelling audit for user:', effectiveUid);
      const result = await cancelAudit(effectiveUid);
      
      if (result.success) {
        setAuditInProgress(false);
        setCurrentAuditUrl('');
        setAuditProgressId(null);
        showNotificationMessage(`✅ Audit cancelled successfully! (${result.cancelledCount} audit(s) cancelled)`, 'success');
        
        // Reload user data to refresh the state
        if (currentUser?.uid) {
          loadUserAudits();
        }
      }
    } catch (error) {
      console.error('❌ Error cancelling audit:', error);
      showNotificationMessage('❌ Failed to cancel audit. Please try again.', 'error');
    }
  };


  // Issue Card Component
  const IssueCard = ({ issue, pagesAffected, fixType }) => {
    return (
      <div className={`p-4 border rounded-lg shadow-sm hover:shadow-md cursor-pointer transition-all ${
        darkMode ? 'bg-gray-800 border-gray-600 hover:border-gray-500' : 'bg-white border-gray-200 hover:border-gray-300'
      }`}>
        <div className="flex justify-between items-start mb-2">
          <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>{issue}</h4>
          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getFixTypeColor(fixType)}`}>
            {fixType}
          </span>
        </div>
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {pagesAffected} page{pagesAffected !== 1 ? 's' : ''} affected
        </p>
      </div>
    );
  };

  // Page Modal Component
  const PageModal = ({ page, onClose }) => {
    if (!page) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className={`max-w-2xl w-full rounded-lg shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} p-6`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className={`text-xl font-bold tracking-tight text-gray-900 dark:text-white`}>Page Details: {page.url}</h3>
            <button 
              onClick={onClose}
              className={`text-2xl ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
            >
              ×
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>SEO Score</label>
                <div className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>{page.seoScore}/100</div>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Word Count</label>
                <div className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>{page.wordCount}</div>
              </div>
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Title Tag</label>
              <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-800'}`}>
                Kitchen Remodeling Services - Professional Design & Installation
              </div>
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Meta Description</label>
              <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-800'}`}>
                Transform your kitchen with our expert remodeling services. Professional design, quality materials, and exceptional craftsmanship.
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>H1 Count</label>
                <div className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>1</div>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Load Time</label>
                <div className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>{page.speed}s</div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end">
            <button 
              onClick={onClose}
              className={`px-4 py-2 rounded-lg border transition-colors duration-200 font-medium ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
     };


   // New Audit Modal — rendered as a plain function (not a nested component) so
   // inputs keep focus while typing triggers parent re-renders
   const renderNewAuditModal = (onClose) => {
     return (
       <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
         <div className={`max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6`}>
            <div className="relative mb-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-10 h-10 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mb-2">
                  <Globe className="w-5 h-5" />
                </div>
                <h3 className={`text-xl font-bold tracking-tight text-gray-900 dark:text-white`}>Run New Website Audit</h3>
              </div>
              <button
                onClick={onClose}
                disabled={isLoading}
                className={`absolute top-0 right-0 text-2xl leading-none ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                ×
              </button>
            </div>
           
           {!isLoading ? (
             <>
               <div className="space-y-4">
                 <div>
                   <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                     Website URL
                   </label>
                   <input
                     type="url"
                     placeholder="https://example.com"
                     value={newAuditUrl}
                     onChange={(e) => setNewAuditUrl(e.target.value)}
                     className={`w-full p-3 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                       darkMode 
                         ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                         : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                     }`}
                   />
                                       <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {accountProfile?.website && newAuditUrl === accountProfile.website
                        ? '✓ Prefilled from your company profile'
                        : 'Enter the full URL including https://'}
                    </p>
                    {!currentUser?.uid && (
                      <p className={`text-xs mt-2 text-red-500`}>
                        ⚠️ You must be logged in to run website audits
                      </p>
                    )}
                 </div>
                 <div>
                   <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                     Target keywords <span className="font-normal opacity-70">(optional)</span>
                   </label>
                   <textarea
                     placeholder="kitchen remodel, home addition, bathroom renovation"
                     value={newAuditKeywords}
                     onChange={(e) => setNewAuditKeywords(e.target.value)}
                     rows={2}
                     className={`w-full p-3 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                       darkMode
                         ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                         : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                     }`}
                   />
                   <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                     Services or phrases your customers search for, separated by commas. We’ll measure these in AI tools too and save them for your next audit.
                   </p>
                 </div>
               </div>
               
               <div className="mt-6 flex justify-end space-x-3">
                 <button 
                   onClick={onClose}
                   className={`px-4 py-2 rounded-lg border transition-colors duration-200 font-medium ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                 >
                   Cancel
                 </button>
                                                     <button 
                    onClick={() => {
                      console.log('🔘 Start Audit button clicked');
                      console.log('URL:', newAuditUrl);
                      console.log('User:', currentUser?.uid);
                      runNewAudit();
                    }}
                    disabled={!newAuditUrl.trim() || !currentUser?.uid}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
                  >
                    {!currentUser?.uid ? 'Please Login First' : 'Start Audit'}
                  </button>
               </div>
             </>
           ) : (
             <div className="space-y-4">
               <div className="text-center">
                 <BBALoader size={48} className="mx-auto mb-4" />
                 <p className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                   {auditProgress || 'Creating audit task...'}
                 </p>
                 <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mt-4`}>
                   Please wait while we set up your audit...
                 </p>
               </div>
             </div>
           )}
         </div>
       </div>
     );
   };

   // Close tooltips when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeTooltip && !event.target.closest('.info-icon-container')) {
        setActiveTooltip(null);
      }
    };

    if (activeTooltip) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [activeTooltip]);

  // Load user's audit history on component mount
  useEffect(() => {
    console.log('🔍 Website component mounted');
    console.log('User:', currentUser);
    console.log('User UID:', currentUser?.uid);
    console.log('User object keys:', currentUser ? Object.keys(currentUser) : 'No user');
    
    if (currentUser?.uid) {
      loadUserAudits();
    } else {
      console.log('❌ No user UID available');
    }
  }, [currentUser, effectiveUid]);



  // Run new audit function
  const runNewAudit = async () => {
    console.log('🔍 Starting website audit...');
    console.log('URL:', newAuditUrl);
    console.log('User:', currentUser?.uid);
    
    if (newAuditUrl.trim() && currentUser?.uid) {
      try {
        setIsLoading(true);
        setAuditProgress('Starting website audit...');
        
        const keywordList = newAuditKeywords
          .split(/[,\n]/)
          .map((s) => s.trim())
          .filter(Boolean);
        const result = await createWebsiteAudit(newAuditUrl, effectiveUid, keywordList);
        console.log('Audit task created:', result);

        // Close modal and reset form
        setShowNewAuditModal(false);
        const auditedUrl = newAuditUrl;
        setNewAuditUrl('');
        setNewAuditKeywords('');
        setAuditProgress(null);
        setIsLoading(false);
        
        // Set audit in progress state
        setAuditInProgress(true);
        setCurrentAuditUrl(auditedUrl);

        // Remember this audit's keywords (and URL, if the profile has none) so future audits prefill
        if (companyId) {
          const accountUpdate = { targetKeywords: keywordList };
          if (!accountProfile?.website) {
            accountUpdate.website = auditedUrl;
          }
          updateAccount(companyId, accountUpdate)
            .then(() => setAccountProfile((prev) => ({ ...(prev || {}), ...accountUpdate })))
            .catch((err) => console.warn('Could not save audit defaults to company profile:', err));
        }

        showNotificationMessage(`🚀 Website audit started for ${auditedUrl}! Results typically arrive in 5–15 minutes.`, 'success');

        // Poll for completion in the background
        if (result.taskId) {
          pollAuditStatus(result.taskId, effectiveUid, (status) => {
            setAuditProgress(`Audit status: ${status}`);
          })
            .then((results) => {
              const transformedResults = transformAuditResults(results);
              setAuditData(transformedResults);
              setAuditInProgress(false);
              setCurrentAuditUrl('');
              setAuditProgressId(null);
              showNotificationMessage('✅ Website audit completed!', 'success');
            })
            .catch((pollError) => {
              console.warn('Audit polling ended:', pollError.message);
            });
        }
        
      } catch (error) {
        console.error('Error sending audit request:', error);
        setAuditProgress(`Error: ${error.message}`);
        
        // Show detailed error information
        let errorMessage = `Error: ${error.message}`;
        if (error.code) {
          errorMessage += `\nCode: ${error.code}`;
        }
        if (error.details) {
          errorMessage += `\nDetails: ${JSON.stringify(error.details)}`;
        }
        
        showNotificationMessage(`❌ Audit request failed:\n${errorMessage}`, 'error');
        
        // Keep modal open on error so user can see the error message
      } finally {
        setIsLoading(false);
      }
    } else {
      console.log('❌ Missing URL or user ID');
      console.log('URL provided:', newAuditUrl);
      console.log('User ID:', currentUser?.uid);
      setAuditProgress('Please enter a valid URL and ensure you are logged in.');
      showNotificationMessage('❌ Please enter a valid URL and ensure you are logged in.', 'error');
    }
  };

  // Load user's audit history and progress
  const loadUserAudits = async () => {
    try {
      console.log('🔄 Loading user audits for:', effectiveUid);
      setDataLoading(true);
      
      // Also try to get the latest marketing request status manually
      try {
        const statusResult = await getWebsiteManagementRequestStatus(effectiveUid);
        console.log('🔄 Manual status check result:', statusResult);
        if (statusResult.success && statusResult.request) {
          setMarketingRequestStatus(statusResult.request);
          console.log('🔄 Set marketingRequestStatus from manual check:', statusResult.request.status);
        }
      } catch (statusError) {
        console.error('🔄 Error in manual status check:', statusError);
      }
      
      // Check for audit progress first
      const progressResult = await getUserAuditProgress(effectiveUid);
      console.log('🔄 Audit progress result:', progressResult);
      
      if (progressResult.success && progressResult.progress) {
        console.log('🔄 Found audit in progress:', progressResult.progress);
        console.log('🔄 Progress accountId:', progressResult.progress.accountId);
        setAuditInProgress(true);
        setCurrentAuditUrl(progressResult.progress.url);
        setAuditProgressId(progressResult.progress.id);
      } else {
        console.log('🔄 No audit in progress');
        setAuditInProgress(false);
        setCurrentAuditUrl('');
        setAuditProgressId(null);
      }
      
      const result = await getUserAudits(effectiveUid);
      console.log('📊 User audits result:', result);
      
      if (result.success && result.audits.length > 0) {
        console.log(`📋 Found ${result.audits.length} audits`);
        console.log('📋 All audits:', result.audits);
        // Load the most recent completed audit
        const latestAudit = result.audits.find(audit => audit.status === 'completed');
        console.log('🔍 Latest completed audit:', latestAudit);
        if (latestAudit && latestAudit.results) {
          console.log('✅ Loading latest completed audit:', latestAudit);
          const transformedResults = transformAuditResults(latestAudit.results);
          console.log('🔄 Transformed results:', transformedResults);
          setAuditData(transformedResults);
          
          // Clear audit in progress if we loaded new data
          if (auditInProgress) {
            setAuditInProgress(false);
            setCurrentAuditUrl('');
            setAuditProgressId(null);
            showNotificationMessage('✅ New audit results loaded!', 'success');
          }
        } else {
          console.log('⚠️ No completed audits found');
          setAuditData(null);
        }
      } else {
        console.log('📭 No audits found for user');
        setAuditData(null);
      }

      // Load management request data (for submittedRequestData only)
      try {
        const websiteData = await getUserWebsiteData(effectiveUid);
        console.log('🔄 Website data loaded:', websiteData);
        
        if (websiteData.managementRequest) {
          setSubmittedRequestData(websiteData.managementRequest);
          setManagementRequestSubmitted(true);
        } else {
          setSubmittedRequestData(null);
          setManagementRequestSubmitted(false);
        }
      } catch (error) {
        console.error('Error loading website management request:', error);
        // Don't set error state for this, as it's not critical
      }
    } catch (error) {
      console.error('❌ Error loading user audits:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details
      });
      setAuditData(null);
    } finally {
      setDataLoading(false);
    }
  };

  // Auto-dismiss audit in progress message after 20 minutes
  useEffect(() => {
    if (auditInProgress) {
      const timer = setTimeout(() => {
        setAuditInProgress(false);
        setCurrentAuditUrl('');
        showNotificationMessage('⏰ Audit in progress message dismissed. Check back later for results.', 'success');
      }, 20 * 60 * 1000); // 20 minutes

      return () => clearTimeout(timer);
    }
  }, [auditInProgress]);

  // Real-time listener for audit progress updates
  useEffect(() => {
    if (!currentUser?.uid) return;

    console.log('🔊 Setting up real-time listener for audit progress');
    console.log('🔊 Current user UID:', effectiveUid);
    
    // Create query for user's audit progress
    const progressQuery = query(
      collection(db, 'users', effectiveUid, 'auditProgress'),
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    
    console.log('🔊 Query created for audit progress for user:', effectiveUid);

    // Set up real-time listener for audit progress
    const unsubscribeProgress = onSnapshot(progressQuery, (querySnapshot) => {
      console.log('🔊 Real-time update received for audit progress');
      console.log('🔊 Query snapshot size:', querySnapshot.size);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const progressData = doc.data();
        
        console.log('🔊 New audit progress data:', progressData);
        
        // If audit is still in progress, update the state
        if (progressData.status === 'in_progress') {
          setAuditInProgress(true);
          setCurrentAuditUrl(progressData.url);
          setAuditProgressId(progressData.id);
          console.log('🔊 Audit still in progress:', progressData.url);
        } else {
          // Audit is no longer in progress, clear the state
          setAuditInProgress(false);
          setCurrentAuditUrl('');
          setAuditProgressId(null);
          console.log('🔊 Audit no longer in progress, clearing state');
          
          // Reload audit data to get the latest results
          loadUserAudits();
        }
      } else {
        // No audit progress found, clear the state
        console.log('🔊 No audit progress found - clearing state');
        setAuditInProgress(false);
        setCurrentAuditUrl('');
        setAuditProgressId(null);
        
        // Reload audit data to get the latest results
        loadUserAudits();
      }
    }, (error) => {
      console.error('🔊 Error in audit progress listener:', error);
    });

    // Cleanup listener on unmount
    return () => {
      console.log('🔊 Cleaning up audit progress listener');
      unsubscribeProgress();
    };
  }, [effectiveUid]);

  // Real-time listener for marketing request status updates
  useEffect(() => {
    if (!currentUser?.uid) return;

    console.log('🔊 Setting up real-time listener for marketing requests');
    console.log('🔊 Current user UID:', effectiveUid);
    
    // Reset initial status load flag when user changes
    setInitialStatusLoad(true);
    
    // First, let's try a simpler query to see if we can find any marketing requests for this user
    const allRequestsQuery = query(
      collection(db, 'marketingRequests'),
      where('userId', '==', effectiveUid)
    );
    
    // Create query for user's website marketing requests (without orderBy to avoid index requirement)
    const requestsQuery = query(
      collection(db, 'marketingRequests'),
      where('userId', '==', effectiveUid),
      where('category', '==', 'website')
    );
    
    console.log('🔊 Query created for user:', effectiveUid, 'category: website');
    
    // Let's also check all requests for this user
    getDocs(allRequestsQuery).then(snapshot => {
      console.log('🔊 All marketing requests for user:', snapshot.docs.map(doc => ({ 
        id: doc.id, 
        category: doc.data().category, 
        status: doc.data().status 
      })));
    }).catch(error => {
      console.error('🔊 Error querying all requests:', error);
    });

    // Set up real-time listener
    const unsubscribe = onSnapshot(requestsQuery, (querySnapshot) => {
      console.log('🔊 Real-time update received for marketing requests');
      console.log('🔊 Query snapshot size:', querySnapshot.size);
      console.log('🔊 Query snapshot docs:', querySnapshot.docs.map(doc => ({ id: doc.id, data: doc.data() })));
      console.log('🔊 Query error (if any):', querySnapshot.metadata.fromCache ? 'from cache' : 'from server');
      
      if (!querySnapshot.empty) {
        // Get the most recent document by sorting in JavaScript
        const docs = querySnapshot.docs;
        const sortedDocs = docs.sort((a, b) => {
          const aTime = a.data().createdAt?.toDate?.() || new Date(a.data().createdAt || 0);
          const bTime = b.data().createdAt?.toDate?.() || new Date(b.data().createdAt || 0);
          return bTime - aTime; // Most recent first
        });
        
        const doc = sortedDocs[0];
        const requestData = doc.data();
        
        console.log('🔊 New marketing request data:', requestData);
        console.log('🔊 Status from request data:', requestData.status);
        console.log('🔊 Full request data keys:', Object.keys(requestData));
        
        // Update states atomically to prevent race conditions
        setManagementRequestSubmitted(true);
        setMarketingRequestStatus({
          id: doc.id,
          ...requestData
        });
        
        console.log('🔊 Set marketingRequestStatus to:', {
          id: doc.id,
          ...requestData
        });
        
        // Mark that we've loaded the initial status
        if (initialStatusLoad) {
          setInitialStatusLoad(false);
        }
        
        // Preserve the submitted request data if it exists, otherwise create it from the marketing request
        setSubmittedRequestData(prevSubmittedData => {
          if (prevSubmittedData) {
            // Keep existing submitted data but update with any new information
            console.log('🔊 Preserving existing submitted data and updating status');
            return {
              ...prevSubmittedData,
              // Update any fields that might have changed
              status: requestData.status,
              updatedAt: requestData.updatedAt || requestData.createdAt
            };
          } else {
            // Create submitted request data from marketing request data
            console.log('🔊 Creating new submitted data from marketing request');
            return {
              selectedServices: requestData.selectedServices || [],
              websiteUrl: requestData.websiteUrl || '',
              monthlyBudget: requestData.budget || requestData.monthlyBudget || '',
              priorityLevel: requestData.priority || requestData.priorityLevel || '',
              submittedAt: requestData.createdAt ? (requestData.createdAt.toDate ? requestData.createdAt.toDate().toISOString() : requestData.createdAt) : new Date().toISOString(),
              status: requestData.status,
              updatedAt: requestData.updatedAt || requestData.createdAt
            };
          }
        });
        
        console.log('🔊 Updated managementRequestSubmitted to true');
        console.log('🔊 Updated marketingRequestStatus to:', requestData.status);
      } else {
        console.log('🔊 No marketing requests found - resetting states');
        console.log('🔊 This might be why marketingRequestStatus is null');
        setMarketingRequestStatus(null);
        setManagementRequestSubmitted(false);
        setSubmittedRequestData(null);
      }
    }, (error) => {
      console.error('🔊 Error in real-time listener:', error);
    });

    // Cleanup listener on unmount
    return () => {
      console.log('🔊 Cleaning up real-time listener');
      unsubscribe();
    };
  }, [effectiveUid]);

  // Separate effect to handle status change notifications
  useEffect(() => {
    if (!marketingRequestStatus || initialStatusLoad) return;
    
    // This will trigger when marketingRequestStatus changes
    console.log('🔊 Marketing request status changed to:', marketingRequestStatus.status);
    console.log('🔊 Full marketingRequestStatus object:', marketingRequestStatus);
    
    // Show notification for status changes (but not on initial load)
    if (marketingRequestStatus.status && managementRequestSubmitted) {
      const statusMessages = {
        'approved': '✅ Your website management request has been approved!',
        'in-progress': '🔄 Your website management request is now in progress!',
        'completed': '🎉 Your website management request has been completed!',
        'denied': '❌ Your website management request has been denied.',
        'cancelled': '⚠️ Your website management request has been cancelled.'
      };
      
      const message = statusMessages[marketingRequestStatus.status];
      if (message) {
        showNotificationMessage(message, marketingRequestStatus.status === 'denied' ? 'error' : 'success');
      }
    }
  }, [marketingRequestStatus?.status, managementRequestSubmitted, initialStatusLoad]);

  // Loading component
  const LoadingState = () => (
    <div className="flex items-center justify-center min-h-96">
      <div className="text-center">
        <BBALoader size={48} className="mx-auto mb-4" />
        <p className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Loading your audit data...
        </p>
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mt-2`}>
          Please wait while we fetch your website audit information.
        </p>
      </div>
    </div>
  );

  // Helper function to get audit data or empty data
  const getAuditData = () => auditData || getEmptyAuditData();

  // Default empty data for when no audit exists
  const getEmptyAuditData = () => ({
    lastScanned: 'No audits yet',
    seoScore: null,
    pageHealth: null,
    contentScore: null,
    onPageScore: null,
    offPageScore: null,
    technicalScore: null,
    visibilityScore: null,
    organicTraffic: {
      amount: 0,
      rankingKeywords: 0,
      estimatedTrafficValue: 0,
      keyInsight: 'No traffic data available'
    },
    backlinkProfile: {
      backlinks: 0,
      spamScore: 0,
      nofollowLinks: 0,
      keyInsight: 'No backlink data available'
    },
    historicalTraffic: {
      averageETV: 0,
      highestETV: 0,
      keywordsPerMonth: 0,
      keyInsight: 'No historical data available'
    },
    rankedKeywords: {
      total: 0,
      top10: 0,
      organicTrafficValue: 0,
      keyInsight: 'No keyword data available'
    },
    pageAudit: {
      onpageScore: 0,
      loadSpeed: 0,
      errorWarnings: 0,
      keyInsight: 'No page audit data available'
    },
    errorsWarnings: {
      criticalErrors: 0,
      warnings: 0,
      notices: 0,
      accessibilityIssues: 0,
      performanceIssues: 0,
      seoIssues: 0,
      keyInsight: 'No error data available'
    },
    aiSearch: null,
    issues: [],
    pages: [],
    opportunities: []
  });

  return (
    <div className={embedded ? '' : 'relative min-h-screen'}>
      {!embedded && <PortalBackground />}
      <div className={`relative max-w-7xl mx-auto ${embedded ? '' : 'p-4 md:p-8'}`}>
        {!embedded && <MarketingPageHeader title="Website Audit" />}

        {/* Notification */}
        {showNotification && (
          <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-md ${
            notificationType === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}>
            <div className="flex items-center">
              <span className="text-lg mr-2">
                {notificationType === 'success' ? '✅' : '❌'}
              </span>
              <p className="text-sm">{notificationMessage}</p>
            </div>
          </div>
        )}

        {dataLoading && <LoadingState />}

        {/* Show audit data (always show cards, with empty data if no audit exists) */}
        {!dataLoading && (
          <>
            {/* Audit In Progress Message */}
            {auditInProgress && (
              <div className={`mb-6 ${darkMode ? 'bg-blue-900 border-blue-500' : 'bg-blue-100 border-blue-500'} rounded-lg shadow-md p-6 border-2`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-4"></div>
                    <div>
                      <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-blue-800'}`}>
                        🔍 Website Audit In Progress
                      </h3>
                      <p className={`text-sm ${darkMode ? 'text-blue-200' : 'text-blue-600'}`}>
                        Auditing: <span className="font-medium">{currentAuditUrl}</span>
                      </p>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-blue-300' : 'text-blue-500'}`}>
                        This may take 5-15 minutes. Results will be available shortly.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleCancelAudit}
                    className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                      darkMode 
                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                        : 'bg-red-500 hover:bg-red-600 text-white'
                    }`}
                    title="Cancel audit"
                  >
                    Cancel Audit
                  </button>
                </div>
              </div>
            )}


        {/* At-a-glance: grades + plain-English summary + AI visibility */}
        {(() => {
          const data = getAuditData();
          const summary = buildPlainSummary(data);
          const highlights = buildHighlights(data);
          const grade = scoreToGrade(data.seoScore);
          const ai = data.aiSearch;
          const hasAi = ai && (((ai.totalAiVolume || 0) > 0) || ((ai.llmMentions?.totalMentions || 0) > 0));
          const hasAudit = data.seoScore != null;
          const simpleCard = `rounded-xl border shadow-sm bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800`;
          const subAreas = [
            { label: 'Technical', score: data.technicalScore ?? data.pageHealth, blurb: 'Speed & crawlability' },
            { label: 'On-Page SEO', score: data.onPageScore ?? data.seoScore, blurb: 'Titles & content' },
            { label: 'Authority', score: data.offPageScore ?? data.pageHealth, blurb: 'Backlinks & trust' },
            { label: 'Visibility', score: data.visibilityScore ?? data.contentScore, blurb: 'Presence in search' },
          ];
          const criticalCount = data.errorsWarnings?.criticalErrors || 0;
          const statTiles = [
            { icon: TrendingUp, tile: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400', value: (data.organicTraffic?.amount || 0).toLocaleString(), label: 'Monthly Visitors', sub: 'people finding you on Google' },
            { icon: Hash, tile: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400', value: (data.rankedKeywords?.total || 0).toLocaleString(), label: 'Keywords Ranking', sub: `${data.rankedKeywords?.top10 || 0} on page one of Google` },
            { icon: Link2, tile: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400', value: (data.backlinkProfile?.backlinks || 0).toLocaleString(), label: 'Backlinks', sub: 'sites linking to you' },
            { icon: AlertTriangle, tile: criticalCount > 0 ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400', value: criticalCount, label: 'Critical Errors', sub: criticalCount > 0 ? 'fix these first' : 'nothing broken' },
          ];
          // Clean empty state (no card background) before the first audit
          if (!hasAudit) {
            return (
              <div className="py-10 text-center mb-10">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-500 mb-4">
                  <Globe className="w-7 h-7" />
                </div>
                <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  {summary.headline}
                </h2>
                <p className={`text-sm max-w-xl mx-auto mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  We'll scan your website for SEO, speed, technical health, backlinks, and AI search visibility, then show you exactly what to improve.
                </p>
                <button
                  onClick={openNewAuditModal}
                  className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  <Search className="w-4 h-4" />
                  Run Your First Audit
                </button>
              </div>
            );
          }
          return (
            <div className="space-y-6 mb-10">
              {/* Grade hero + area report card */}
              <div className={`relative ${simpleCard} p-8`}>
                <button
                  onClick={openNewAuditModal}
                  aria-label="Run New Audit"
                  title="Run New Audit"
                  className="absolute top-4 right-4 inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 text-sm font-medium"
                >
                  <Search className="w-4 h-4" />
                  <span className="hidden sm:inline">Run New Audit</span>
                </button>
                <div className="flex flex-col items-center text-center">
                  <div className={`flex items-center justify-center w-28 h-28 rounded-full border-4 text-5xl font-extrabold mb-4 ${gradeColorClasses(grade.color)}`}>
                    {grade.letter}
                  </div>
                  <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {summary.headline}
                  </h2>
                  <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Overall search health score: {data.seoScore}/100
                    </span>
                    <span className={`hidden sm:inline text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>•</span>
                    <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Last scanned: {getAuditData().lastScanned}
                    </span>
                  </div>
                  {(
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8 w-full max-w-3xl">
                      {subAreas.map((area) => {
                        const g = scoreToGrade(area.score);
                        return (
                          <div key={area.label} className={`p-4 text-center rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                            <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full border-2 text-lg font-bold mb-2 ${gradeColorClasses(g.color)}`}>
                              {g.letter}
                            </div>
                            <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{area.label}</div>
                            <div className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{area.blurb}</div>
                            <div className={`text-xs mt-1 font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {area.score != null ? `${area.score}/100` : '—'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Key numbers at a glance */}
              {hasAudit && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {statTiles.map((tile) => {
                    const Icon = tile.icon;
                    return (
                      <div key={tile.label} className={`${simpleCard} p-5 text-center`}>
                        <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg mb-2 ${tile.tile}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>{tile.value}</div>
                        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{tile.label}</div>
                        <div className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{tile.sub}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* What's working / needs attention */}
              {hasAudit && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className={`${simpleCard} p-6`}>
                    <h3 className={`text-lg font-bold mb-4 text-center flex items-center justify-center gap-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      What's Working
                    </h3>
                    <ul className="space-y-3">
                      {highlights.working.length > 0 ? highlights.working.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{item}</span>
                        </li>
                      )) : (
                        <li className={`text-sm text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Nothing stands out yet — the priorities below are the fastest path to wins.
                        </li>
                      )}
                    </ul>
                  </div>
                  <div className={`${simpleCard} p-6`}>
                    <h3 className={`text-lg font-bold mb-4 text-center flex items-center justify-center gap-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                      Needs Attention
                    </h3>
                    <ul className="space-y-3">
                      {highlights.attention.length > 0 ? highlights.attention.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{item}</span>
                        </li>
                      )) : (
                        <li className={`text-sm text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Nothing urgent — keep up the good work!
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {/* Top priorities */}
              {summary.actions.length > 0 && (
                <div className={`${simpleCard} p-6`}>
                  <h3 className={`text-xl font-bold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {summary.actions.length === 1 ? 'Your Top Priority' : `Your Top ${summary.actions.length} Priorities`}
                  </h3>
                  <div className="space-y-3 max-w-2xl mx-auto">
                    {summary.actions.map((a, i) => (
                      <div key={i} className={`flex items-start p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                        <div className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full mr-3 text-sm font-bold bg-red-500 text-white">
                          {i + 1}
                        </div>
                        <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Search Visibility highlight */}
              <div className={`${simpleCard} p-6`}>
                <h3 className={`text-xl font-bold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'} flex items-center justify-center`}>
                  🤖 AI Search Visibility
                  <InfoIcon
                    description="More and more customers ask AI tools like ChatGPT and Google's AI answers for recommendations. This shows how often your keywords are searched inside AI tools, and whether those tools currently mention your business."
                    tooltipId="simple-ai"
                  />
                </h3>
                {hasAi ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                    <div className={`p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                      <div className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                        {(ai.totalAiVolume || 0).toLocaleString()}
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Monthly AI searches for your keywords</div>
                    </div>
                    <div className={`p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                      <div className={`text-2xl font-bold ${ai.llmMentions?.cited ? 'text-green-500' : (darkMode ? 'text-gray-300' : 'text-gray-500')}`}>
                        {ai.llmMentions?.cited ? 'Yes' : 'Not yet'}
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Do AI tools recommend you?</div>
                    </div>
                    <div className={`p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                      <div className={`text-lg font-bold tracking-tight text-gray-900 dark:text-white`}>
                        {ai.topKeyword?.keyword || '—'}
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Top AI-searched keyword</div>
                    </div>
                  </div>
                ) : (
                  <p className={`text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    AI search insights will appear here after your next audit — including whether customers find you through tools like ChatGPT and Google's AI answers.
                  </p>
                )}

                {/* Supporting detail: keyword volume table */}
                {hasAi && (ai.keywords || []).length > 0 && (
                  <div className="mt-6">
                    <h4 className={`font-semibold mb-3 text-sm text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      AI Keyword Search Volume
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={`border-b ${darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                            <th className="text-left py-2 font-medium">Keyword</th>
                            <th className="text-right py-2 font-medium">AI Searches / mo</th>
                            <th className="text-center py-2 font-medium">12-Month Trend</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ai.keywords.slice(0, 15).map((k, i) => (
                            <tr key={i} className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                              <td className={`py-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{k.keyword}</td>
                              <td className={`py-2 text-right font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                                {(k.aiSearchVolume || 0).toLocaleString()}
                              </td>
                              <td className="py-2 text-center whitespace-nowrap">
                                <Sparkline trend={k.trend} />
                                <span className={`ml-2 font-bold ${
                                  k.trendDirection === 'up' ? 'text-green-500'
                                    : k.trendDirection === 'down' ? 'text-red-500'
                                      : (darkMode ? 'text-gray-400' : 'text-gray-500')
                                }`}>
                                  {k.trendDirection === 'up' ? '↑' : k.trendDirection === 'down' ? '↓' : '→'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Supporting detail: brand mentions + competitor domains */}
                {hasAi && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
                    <div className={`p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                      <h4 className={`font-semibold mb-3 text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        Brand Mentions in AI Answers
                      </h4>
                      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                        <div className={`p-2 rounded ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
                          <div className={`text-lg font-bold tracking-tight text-gray-900 dark:text-white`}>
                            {ai.llmMentions?.totalMentions || 0}
                          </div>
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total</div>
                        </div>
                        <div className={`p-2 rounded ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
                          <div className={`text-lg font-bold tracking-tight text-gray-900 dark:text-white`}>
                            {ai.llmMentions?.platforms?.google || 0}
                          </div>
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Google AI</div>
                        </div>
                        <div className={`p-2 rounded ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
                          <div className={`text-lg font-bold tracking-tight text-gray-900 dark:text-white`}>
                            {ai.llmMentions?.platforms?.chat_gpt || 0}
                          </div>
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>ChatGPT</div>
                        </div>
                      </div>
                      {(ai.llmMentions?.sampleQuestions || []).length > 0 ? (
                        <div className="space-y-2">
                          <div className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            AI questions where you appear:
                          </div>
                          {ai.llmMentions.sampleQuestions.map((q, i) => (
                            <div key={i} className={`text-sm italic ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              “{q.question}”
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Your brand isn’t cited in AI answers yet — a clear opportunity to capture AI-driven traffic.
                        </p>
                      )}
                    </div>

                    <div className={`p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                      <h4 className={`font-semibold mb-3 text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        Top Domains AI Tools Cite
                      </h4>
                      {(ai.competitors || []).length > 0 ? (
                        <div className="space-y-2">
                          {ai.competitors.map((c, i) => (
                            <div key={i} className={`flex items-center justify-between p-2 rounded ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
                              <span className={`text-sm truncate ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                {i + 1}. {c.domain}
                              </span>
                              <span className={`text-sm font-medium ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {(c.mentions || 0).toLocaleString()} mentions
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          No competitor citation data available for these keywords.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <p className={`text-center text-sm mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  💡 {ai?.keyInsight || 'AI search is a fast-growing way customers discover local businesses.'}
                </p>

                {devMode && <AiSearchDevDiagnostics aiSearch={ai} />}
              </div>
            </div>
          );
        })()}

        {/* TECHNICAL DEEP DIVE: the supporting detail behind the scores above */}
        {getAuditData().seoScore != null && (
         <>
        <div className="pt-2 pb-8 flex items-center gap-4">
          <div className={`flex-1 border-t ${darkMode ? 'border-gray-800' : 'border-gray-200'}`} />
          <div className="text-center">
            <h2 className={`text-sm font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Technical Deep Dive
            </h2>
            <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              The detailed numbers behind your scores
            </p>
          </div>
          <div className={`flex-1 border-t ${darkMode ? 'border-gray-800' : 'border-gray-200'}`} />
        </div>

        {/* Analytics Sections Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Organic Traffic Section */}
          <div className={`rounded-xl border shadow-sm p-6 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800`}>
            <h2 className={`text-base font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'} flex items-center justify-center`}>
              Organic Traffic
              <InfoIcon 
                description="Organic traffic refers to visitors who find your website through unpaid search engine results. This section shows the estimated amount of organic traffic, the number of keywords your site ranks for, and the estimated traffic value. Higher organic traffic typically indicates better SEO performance and more visibility in search results."
                tooltipId="organic-traffic"
              />
            </h2>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
                <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-blue-800'}`}>
                  {getAuditData().organicTraffic.amount.toLocaleString()}
                </div>
                <div className={`text-xs ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>Amount</div>
              </div>
              <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-green-900' : 'bg-green-100'}`}>
                <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-green-800'}`}>
                  {getAuditData().organicTraffic.rankingKeywords}
                </div>
                <div className={`text-xs ${darkMode ? 'text-green-300' : 'text-green-600'}`}>Ranking Keywords</div>
              </div>
              <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-purple-900' : 'bg-purple-100'}`}>
                <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-purple-800'}`}>
                  ${getAuditData().organicTraffic.estimatedTrafficValue.toLocaleString()}
                </div>
                <div className={`text-xs ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>Estimated Traffic Value</div>
              </div>
            </div>
            <div className="text-center">
              <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                💡 {getAuditData().organicTraffic.keyInsight}
              </div>
            </div>
          </div>

          {/* Backlink Profile Section */}
          <div className={`rounded-xl border shadow-sm p-6 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800`}>
            <h2 className={`text-base font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'} flex items-center justify-center`}>
              Backlink Profile
              <InfoIcon 
                description="Backlinks are links from other websites pointing to your site. They're a crucial ranking factor for SEO. This section shows the total number of backlinks, spam score (lower is better), and nofollow links. Quality backlinks from authoritative sites can significantly improve your search rankings, while spammy backlinks can hurt your site's reputation."
                tooltipId="backlink-profile"
              />
            </h2>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
                <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-blue-800'}`}>
                  {getAuditData().backlinkProfile.backlinks}
                </div>
                <div className={`text-xs ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>Backlinks</div>
              </div>
              <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-green-900' : 'bg-green-100'}`}>
                <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-green-800'}`}>
                  {getAuditData().backlinkProfile.spamScore}%
                </div>
                <div className={`text-xs ${darkMode ? 'text-green-300' : 'text-green-600'}`}>Spam Score</div>
              </div>
              <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-purple-900' : 'bg-purple-100'}`}>
                <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-purple-800'}`}>
                  {getAuditData().backlinkProfile.nofollowLinks}
                </div>
                <div className={`text-xs ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>Nofollow Links</div>
              </div>
            </div>
            <div className="text-center">
              <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                💡 {getAuditData().backlinkProfile.keyInsight}
              </div>
            </div>
          </div>

          {/* Historical Traffic Section */}
          <div className={`rounded-xl border shadow-sm p-6 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800`}>
            <h2 className={`text-base font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'} flex items-center justify-center`}>
              Historical Traffic
              <InfoIcon 
                description="Historical traffic data shows trends in your website's organic search performance over time. ETV (Estimated Traffic Value) represents the potential value of organic traffic. This section displays average and peak ETV values, plus the number of keywords ranking per month. Tracking historical data helps identify growth trends and seasonal patterns."
                tooltipId="historical-traffic"
              />
            </h2>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
                <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-blue-800'}`}>
                  ${getAuditData().historicalTraffic.averageETV.toLocaleString()}
                </div>
                <div className={`text-xs ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>Average ETV</div>
              </div>
              <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-green-900' : 'bg-green-100'}`}>
                <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-green-800'}`}>
                  ${getAuditData().historicalTraffic.highestETV.toLocaleString()}
                </div>
                <div className={`text-xs ${darkMode ? 'text-green-300' : 'text-green-600'}`}>Highest ETV</div>
              </div>
              <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-purple-900' : 'bg-purple-100'}`}>
                <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-purple-800'}`}>
                  {getAuditData().historicalTraffic.keywordsPerMonth}
                </div>
                <div className={`text-xs ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>Keywords/Month</div>
              </div>
            </div>
            <div className="text-center">
              <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                💡 {getAuditData().historicalTraffic.keyInsight}
              </div>
            </div>
          </div>

          {/* Ranked Keywords Section */}
          <div className={`rounded-xl border shadow-sm p-6 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800`}>
            <h2 className={`text-base font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'} flex items-center justify-center`}>
              Ranked Keywords
              <InfoIcon 
                description="Ranked keywords are search terms that your website appears for in search engine results. This section shows the total number of keywords you're ranking for, how many are in the top 10 positions (most valuable), and the organic traffic value these keywords generate. Ranking for more relevant keywords in top positions can significantly increase your organic traffic."
                tooltipId="ranked-keywords"
              />
            </h2>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
                <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-blue-800'}`}>
                  {getAuditData().rankedKeywords.total.toLocaleString()}
                </div>
                <div className={`text-xs ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>Total</div>
              </div>
              <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-green-900' : 'bg-green-100'}`}>
                <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-green-800'}`}>
                  {getAuditData().rankedKeywords.top10}
                </div>
                <div className={`text-xs ${darkMode ? 'text-green-300' : 'text-green-600'}`}>Top 10</div>
              </div>
              <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-purple-900' : 'bg-purple-100'}`}>
                <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-purple-800'}`}>
                  ${getAuditData().rankedKeywords.organicTrafficValue.toLocaleString()}
                </div>
                <div className={`text-xs ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>Organic Traffic Value</div>
              </div>
            </div>
            <div className="text-center">
              <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                💡 {getAuditData().rankedKeywords.keyInsight}
              </div>
            </div>
          </div>

                     {/* Page Audit Section */}
           <div className={`rounded-xl border shadow-sm p-6 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800`}>
             <h2 className={`text-base font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'} flex items-center justify-center`}>
               Page Audit
               <InfoIcon 
                 description="Page audit evaluates individual pages on your website for SEO and performance issues. The onpage score measures how well a page is optimized, load speed indicates how quickly pages load (critical for user experience and SEO), and error/warnings show technical issues that need attention. Faster, well-optimized pages rank better and provide better user experience."
                 tooltipId="page-audit"
               />
             </h2>
             <div className="grid grid-cols-3 gap-3 mb-4">
               <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
                 <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-blue-800'}`}>
                   {getAuditData().pageAudit.onpageScore}/100
                 </div>
                 <div className={`text-xs ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>Onpage Score</div>
               </div>
               <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-green-900' : 'bg-green-100'}`}>
                 <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-green-800'}`}>
                   {getAuditData().pageAudit.loadSpeed}s
                 </div>
                 <div className={`text-xs ${darkMode ? 'text-green-300' : 'text-green-600'}`}>Load Speed</div>
               </div>
               <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-purple-900' : 'bg-purple-100'}`}>
                 <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-purple-800'}`}>
                   {getAuditData().pageAudit.errorWarnings}
                 </div>
                 <div className={`text-xs ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>Error/Warnings</div>
               </div>
             </div>
             <div className="text-center">
               <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                 💡 {getAuditData().pageAudit.keyInsight}
               </div>
             </div>
           </div>

         </div>

         {/* Errors/Warnings Section - Full Width */}
         <div className={`mb-8 rounded-xl border shadow-sm p-6 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800`}>
           <h2 className={`text-base font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'} flex items-center justify-center`}>
             Errors/Warnings
             <InfoIcon 
               description="This section categorizes all issues found on your website. Critical errors are serious problems that need immediate attention (broken links, server errors). Warnings are less severe but should be addressed. Notices are minor suggestions. Accessibility issues affect users with disabilities. Performance issues slow down your site. SEO issues impact search rankings. Fixing these improves both user experience and search visibility."
               tooltipId="errors-warnings"
             />
           </h2>
           <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
             <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-red-900' : 'bg-red-100'}`}>
               <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-red-800'}`}>
                 {getAuditData().errorsWarnings.criticalErrors}
               </div>
               <div className={`text-xs ${darkMode ? 'text-red-300' : 'text-red-600'}`}>Critical Errors</div>
             </div>
             <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-yellow-900' : 'bg-yellow-100'}`}>
               <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-yellow-800'}`}>
                 {getAuditData().errorsWarnings.warnings}
               </div>
               <div className={`text-xs ${darkMode ? 'text-yellow-300' : 'text-yellow-600'}`}>Warnings</div>
             </div>
             <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
               <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-blue-800'}`}>
                 {getAuditData().errorsWarnings.notices}
               </div>
               <div className={`text-xs ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>Notices</div>
             </div>
             <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-orange-900' : 'bg-orange-100'}`}>
               <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-orange-800'}`}>
                 {getAuditData().errorsWarnings.accessibilityIssues}
               </div>
               <div className={`text-xs ${darkMode ? 'text-orange-300' : 'text-orange-600'}`}>Accessibility Issues</div>
             </div>
             <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-purple-900' : 'bg-purple-100'}`}>
               <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-purple-800'}`}>
                 {getAuditData().errorsWarnings.performanceIssues}
               </div>
               <div className={`text-xs ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>Performance Issues</div>
             </div>
             <div className={`text-center p-3 rounded-lg ${darkMode ? 'bg-green-900' : 'bg-green-100'}`}>
               <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-green-800'}`}>
                 {getAuditData().errorsWarnings.seoIssues}
               </div>
               <div className={`text-xs ${darkMode ? 'text-green-300' : 'text-green-600'}`}>SEO Issues</div>
             </div>
           </div>
         </div>

         </>
        )}

         {/* Website Management Request Popup */}
         {showManagementPopup && (
           <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
             <div className={`max-w-2xl w-full rounded-lg shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} p-6 max-h-[80vh] overflow-y-auto relative`}>
              <div className="mb-4">
                <h3 className={`text-lg font-semibold text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Website Management
                </h3>
                 <button
                   onClick={() => setShowManagementPopup(false)}
                   className={`absolute top-4 right-4 text-2xl ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                 >
                   ×
                 </button>
               </div>

               {managementRequestSubmitted && submittedRequestData ? (
                 // Show submitted request details
                 <div className="space-y-6">
                   {/* Request Summary */}
                   <div className={`p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                     <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                       Request Summary
                     </h4>
                     <div className="space-y-2">
                       <div className="flex justify-between">
                         <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                           Submitted:
                         </span>
                         <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                           {new Date(submittedRequestData.submittedAt).toLocaleDateString()}
                         </span>
                       </div>
                       <div className="flex justify-between items-center">
                         <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                           Status:
                         </span>
                         <div className="flex items-center">
                           {marketingRequestStatus ? (
                             getStatusBadge(marketingRequestStatus.status)
                           ) : submittedRequestData?.status ? (
                             getStatusBadge(submittedRequestData.status)
                           ) : (
                             getStatusBadge('requested')
                           )}
                         </div>
                       </div>
                     </div>
                   </div>

                   {/* Website Management Details */}
                   <div>
                     <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                       Website Management Details
                     </h4>
                     <div className="space-y-4">
                       <div className={`p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                         <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                           Website URL
                         </div>
                         <div className={`text-lg font-bold tracking-tight text-gray-900 dark:text-white`}>
                           {submittedRequestData.websiteUrl || 'Not provided'}
                         </div>
                       </div>
                       {(submittedRequestData.selectedServices?.length > 0) && (
                         <div className={`p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                           <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                             Selected service
                           </div>
                           <div className={`text-lg font-bold tracking-tight text-gray-900 dark:text-white`}>
                             {submittedRequestData.selectedServices[0]}
                           </div>
                         </div>
                       )}
                     </div>
                   </div>

                   {/* Action Buttons */}
                   <div className="flex space-x-3 pt-4">
                     {marketingRequestStatus?.status === 'requested' && (
                       <button
                         onClick={async () => {
                           if (!currentUser) {
                             setError('You must be logged in to cancel a request');
                             return;
                           }
                           
                           try {
                             setError(null);
                             
                             // Cancel the request in database
                             await cancelWebsiteManagementRequest(effectiveUid);
                             
                             // Update local state
                             setManagementRequestSubmitted(false);
                             setSubmittedRequestData(null);
                             setMarketingRequestStatus(null);
                             setShowManagementPopup(false);
                             
                             showNotificationMessage('✅ Website management request cancelled successfully!', 'success');
                           } catch (err) {
                             console.error('Error canceling management request:', err);
                             showNotificationMessage('❌ Failed to cancel management request. Please try again.', 'error');
                           }
                         }}
                         className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                       >
                         Cancel Request
                       </button>
                     )}
                   </div>
                 </div>
               ) : (
                // Show form for new request (now used to capture details before Stripe checkout)
                <form
                  onSubmit={(e) => {
                    // Prevent default submit; Stripe checkout is triggered by explicit buttons
                    e.preventDefault();
                  }}
                  className="space-y-6"
                >
                   {/* Website URL */}
                   <div>
                     <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                       Website URL
                     </label>
                     <input
                       type="url"
                       value={managementFormData.websiteUrl}
                       onChange={(e) => setManagementFormData(prev => ({ ...prev, websiteUrl: e.target.value }))}
                       className={`w-full p-2 border rounded ${
                         darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                       }`}
                       placeholder="https://example.com"
                     />
                   </div>

                  {/* Single-select: a la carte options (SEO Management, Templated Website + SEO, Custom Website + SEO) */}
                  <div>
                    <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      Choose one service
                    </h4>
                     {websiteAlaCarteLoading ? (
                       <div className="flex justify-center py-4">
                         <BBALoader size={32} />
                       </div>
                     ) : (
                       <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                         {WEBSITE_ALA_CARTE_NAMES.map((name) => {
                           const item =
                             websiteAlaCarteOptions.find(
                               (opt) =>
                                 typeof opt.name === 'string' &&
                                 opt.name.trim().toLowerCase() === name.trim().toLowerCase()
                             ) || {
                               name,
                               description: '',
                               icon: '🌐'
                             };
                           const isSelected = managementFormData.selectedService === name;
                           const priceLabel = getWebsiteAlaCartePrice(item);
                           const descriptionText =
                             (item.description && item.description.trim()) ||
                             (name === 'SEO Management' ? SEO_MANAGEMENT_FALLBACK_DESCRIPTION : '');
                          return (
                            <button
                              key={item.id || name}
                              type="button"
                              onClick={() =>
                                setManagementFormData((prev) => ({
                                  ...prev,
                                  selectedService: prev.selectedService === name ? '' : name
                                }))
                              }
                              className={`relative p-4 rounded-lg border transition-colors text-center ${
                                 isSelected
                                   ? darkMode
                                     ? 'border-red-500 bg-red-900/20'
                                     : 'border-red-500 bg-red-50'
                                   : darkMode
                                     ? 'border-gray-600 hover:bg-gray-700'
                                     : 'border-gray-300 hover:bg-gray-50'
                               }`}
                             >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <div className="text-2xl mb-1 flex items-center justify-center">
                                    {item.icon || '🌐'}
                                  </div>
                                  <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                                    {name}
                                  </div>
                                </div>
                              </div>
                               {priceLabel && (
                                 <div
                                   className={`text-sm font-semibold text-center ${
                                     darkMode ? 'text-red-300' : 'text-red-600'
                                   }`}
                                 >
                                   {priceLabel}
                                 </div>
                               )}
                             </button>
                           );
                       })}
                      </div>
                    )}
                  </div>

                  {/* Start Date under selected service cards */}
                  <div>
                    <h4 className={`font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      Start Date
                    </h4>
                    <input
                      type="date"
                      value={managementFormData.startDate}
                      min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                      onChange={(e) =>
                        setManagementFormData((prev) => ({
                          ...prev,
                          startDate: e.target.value,
                        }))
                      }
                      className={`w-full p-2 border rounded text-sm ${
                        darkMode
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>

                  {websiteManagementCheckoutError && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                      {websiteManagementCheckoutError}
                    </p>
                  )}

                  {/* Payment Buttons (Stripe Checkout) */}
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => handleWebsiteManagementCheckout('ach')}
                      disabled={websiteManagementCheckoutLoading}
                      className="flex-1 py-3 px-4 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center gap-2"
                    >
                      <Landmark className="w-6 h-6" />
                      <span>Pay with Bank Account (ACH)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleWebsiteManagementCheckout('card')}
                      disabled={websiteManagementCheckoutLoading}
                      className="flex-1 py-3 px-4 rounded-lg border-2 border-red-500 bg-red-600 hover:bg-red-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center gap-2"
                    >
                      <CreditCard className="w-6 h-6" />
                      <span>Pay with Credit Card (3% Fee)</span>
                    </button>
                  </div>
                 </form>
               )}
             </div>
           </div>
         )}

          </>
        )}

        {/* Modals – always rendered */}
        {showPageModal && (
          <PageModal 
            page={selectedPage} 
            onClose={() => {
              setShowPageModal(false);
              setSelectedPage(null);
            }} 
          />
        )}
        {showNewAuditModal && renderNewAuditModal(() => {
          setShowNewAuditModal(false);
          setNewAuditUrl('');
          setNewAuditKeywords('');
        })}
      </div>
    </div>
  );
};

export default Website; 